import React, { FC, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import Navbar from '../../components/Navbar';
import HeaderHome from '../../components/HeaderHome';
import WhyHome from '../../components/WhyHome';
import Guide from '../../components/Guide';
import ReadytoGetStarted from '../../components/ReadytoGetStarted';
import Footer from '../../components/Footer';
import Faq from '../../components/Faq';
import InterlinkSection from '../../components/InterlinkSection';
import { useMPCWidget } from '../../helpers/useMPCWidget';
import {
  cryptoData,
  cryptoSectionTranslations,
  getCryptoUrl,
} from '../../data/cryptoData';

interface Props {
  data: Queries.SeoPageQuery;
  locale: string;
  crypto: string;
  provider?: string;
  showLoginWidget?: boolean;
}

const PageContent: FC<Props> = ({
  data,
  locale,
  crypto,
  provider,
  showLoginWidget,
}) => {
  const { json } = data;
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { hasAccount, isLoading, user } = useMPCWidget();

  const localeKey = (locale as 'en' | 'es' | 'ru' | 'zh') || 'en';
  const cryptoLinkPrefix =
    cryptoSectionTranslations.linkPrefix[localeKey] || 'Sell';

  const cryptoLinks = useMemo(
    () =>
      cryptoData
        .filter(c => c.slug !== crypto)
        .map(c => ({
          slug: c.slug,
          label: `${cryptoLinkPrefix} ${c.names[localeKey] || c.names.en}`,
          url: getCryptoUrl(c.slug, locale),
        })),
    [locale, localeKey, cryptoLinkPrefix, crypto]
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  useEffect(() => {
    const handleLoad = () => {
      setTimeout(() => {
        setShowVideo(true);
      }, 2000);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <>
      <Helmet>
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Chivo_Mono/ChivoMono-VariableFont_wght.ttf"
          as="font"
          type="font/ttf"
          crossOrigin="anonymous"
        />

        <style>
          {`
            @font-face {
              font-family: 'Elza Condensed';
              src: url('https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff') format('woff');
              font-style: normal;
              font-display: swap;
            }
            @font-face {
              font-family: 'Satoshi Variable Medium';
              src: url('https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff') format('woff');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
            @font-face {
              font-family: 'Chivo Mono';
              src: url('https://fonts.bitcoin.com/Chivo_Mono/ChivoMono-VariableFont_wght.ttf') format('truetype');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
          `}
        </style>
        <script
          src={
            process.env.MPC_WIDGET_URL ||
            'https://widget.bitcoin.com/widget.v0.7.14.iife.js'
          }
          type="text/javascript"
        />
      </Helmet>
      <Navbar navbar={json?.navbar || null} locale={locale} />
      <main>
        <HeaderHome
          headerHome={json?.headerHome || null}
          crypto={crypto}
          provider={provider}
          showLoginWidget={showLoginWidget}
          isLoading={isLoading}
          hasAccount={hasAccount}
          user={user}
          showVideo={showVideo}
        />
        <WhyHome whyHome={json?.whyHome || null} />
        <Guide
          guide={json?.guide || null}
          hasScrolled={hasScrolled}
          showVideo={showVideo}
        />
        <ReadytoGetStarted
          readytogetstarted={json?.readytogetstarted || null}
          // hasScrolled={hasScrolled}
        />
        <InterlinkSection
          title={
            cryptoSectionTranslations.cryptoPage.title[localeKey] ||
            'Other cryptocurrencies available to sell'
          }
          subtitle={
            cryptoSectionTranslations.cryptoPage.subtitle[localeKey] ||
            'Explore additional crypto assets ready to be sold instantly at Bitcoin.com'
          }
          items={cryptoLinks}
        />
        {json?.faq && <Faq faq={json?.faq || null} />}
      </main>

      <Footer footer={json?.footer || null} />
      {/* hasScrolled={hasScrolled} */}
    </>
  );
};

export default PageContent;
